import { z } from 'zod';

/**
 * What a session's sharing starts as, org-wide: private to whoever launched
 * it, or already shared with the whole organization at one of the two share
 * access levels.
 *
 * This is the org's *default*, not its ceiling — the two answer different
 * questions and only the ceiling (`SessionSharingPolicy`) is a security
 * control. The default is a convenience ("our team works in the open, stop
 * making everyone ask"), applied by materialising an ordinary org-subject
 * `resource_grants` row when the session is created. That choice is what
 * keeps it honest: access to a session is still explained by a grant row,
 * the grant is revocable and auditable through the surfaces that already
 * exist, and — because the default is read once, at creation — changing it
 * later never reaches back and re-opens or strips sessions that already
 * exist. Narrowing the default, like narrowing the ceiling, only affects the
 * next session.
 *
 * The vocabulary lives in its own module, apart from the helpers in
 * `session-sharing.ts` that interpret it, because `organizations` carries
 * the column: `organization.types.ts` needs the schema, and reaching it
 * through `session-sharing.ts` would drag in `resource-grant.types.ts`,
 * which imports `RoleIdSchema` back out of `organization.types.ts`. Zod
 * schemas are evaluated at module load, so that cycle is a TDZ crash at
 * import time rather than a type error.
 */
export const DEFAULT_SESSION_SHARE_VALUES = ['private', 'view', 'edit'] as const;
export type DefaultSessionShare = (typeof DEFAULT_SESSION_SHARE_VALUES)[number];
export const DefaultSessionShareSchema = z
  .enum(DEFAULT_SESSION_SHARE_VALUES)
  .meta({ id: 'DefaultSessionShare' });

/** How sessions start for an org that has not chosen a default. */
export const DEFAULT_SESSION_SHARE: DefaultSessionShare = 'private';
